import { useQuery } from "@tanstack/react-query";
import type { OrderStatusCountsResponse } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { queryKeys, type QueryKeyFilters } from "@/lib/query/core/query-keys";
import type { OrdersQueryPaymentMethod } from "./useOrdersQuery";

/** Structured filters the status-counts facet can be scoped to. Omit (or leave
 *  empty) for the store-wide counts that power the tab pills. */
export type OrderStatusCountsFilters = {
  q?: string;
  governorateIds?: string[];
  dateFrom?: string;
  dateTo?: string;
  paymentMethod?: OrdersQueryPaymentMethod;
  shipmentStatus?: string;
};

type UseSellerOrderStatusCountsQueryParams = {
  storeSlug: string;
  filters?: OrderStatusCountsFilters;
};

function hasAnyFilter(f: OrderStatusCountsFilters): boolean {
  return Boolean(
    f.q ||
      (f.governorateIds && f.governorateIds.length > 0) ||
      f.dateFrom ||
      f.dateTo ||
      f.paymentMethod ||
      f.shipmentStatus,
  );
}

/** Primitive-only projection for the query key (arrays → CSV). */
function toKeyFilters(f: OrderStatusCountsFilters): QueryKeyFilters {
  return {
    q: f.q,
    governorateIds: f.governorateIds?.length ? f.governorateIds.join(",") : undefined,
    dateFrom: f.dateFrom,
    dateTo: f.dateTo,
    paymentMethod: f.paymentMethod,
    shipmentStatus: f.shipmentStatus,
  };
}

function buildPath(f?: OrderStatusCountsFilters): string {
  if (!f) return endpoints.seller.orderStatusCounts;
  const query = new URLSearchParams();
  if (f.q) query.set("q", f.q);
  if (f.governorateIds?.length) query.set("governorateIds", f.governorateIds.join(","));
  if (f.dateFrom) query.set("dateFrom", f.dateFrom);
  if (f.dateTo) query.set("dateTo", f.dateTo);
  if (f.paymentMethod) query.set("paymentMethod", f.paymentMethod);
  if (f.shipmentStatus) query.set("shipmentStatus", f.shipmentStatus);
  const qs = query.toString();
  return qs ? `${endpoints.seller.orderStatusCounts}?${qs}` : endpoints.seller.orderStatusCounts;
}

export function useOrderStatusCountsQuery({
  storeSlug,
  filters,
}: UseSellerOrderStatusCountsQueryParams) {
  // Empty/omitted filters → the store-wide request (identical key + path to
  // before), so existing callers (tab pills) are unaffected.
  const active = filters && hasAnyFilter(filters) ? filters : undefined;
  const query = useQuery({
    queryKey: queryKeys.seller.orderStatusCounts(
      storeSlug,
      active ? toKeyFilters(active) : undefined,
    ),
    queryFn: () => apiJsonClient<OrderStatusCountsResponse>(buildPath(active)),
    enabled: storeSlug.length > 0,
    // Buyer orders do not invalidate this portal's existing summary cache.
    refetchOnMount: "always",
    });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
